import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { DEFAULT_LANDING_ROUTE } from '@/config/featureFlags';

/**
 * 8 main-nav routes per the V6.0 prototype + the auth surface from
 * Phase 0.7. Auth routes carry `meta.layout = 'auth'` (renders bare,
 * outside the AppShell) and `meta.public = true` (skips the auth guard).
 */
const routes: RouteRecordRaw[] = [
  // Home is hidden in production (src/config/featureFlags.ts), so the
  // default landing route flips to 'people' there — dev/staging keep
  // landing on Home.
  { path: '/', redirect: { name: DEFAULT_LANDING_ROUTE } },

  // --- Auth surface ---
  {
    path: '/welcome',
    name: 'welcome',
    component: () => import('@/auth/WelcomePage.vue'),
    meta: { layout: 'auth', public: true },
  },
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('@/auth/SignInPage.vue'),
    meta: { layout: 'auth', public: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/auth/ForgotPasswordPage.vue'),
    meta: { layout: 'auth', public: true },
  },
  {
    path: '/confirm-forgot-password',
    name: 'confirm-forgot-password',
    component: () => import('@/auth/ConfirmForgotPasswordPage.vue'),
    meta: { layout: 'auth', public: true },
  },
  {
    path: '/force-change-password',
    name: 'force-change-password',
    component: () => import('@/auth/ForceChangePasswordPage.vue'),
    meta: { layout: 'auth', public: true },
  },
  {
    path: '/mfa',
    name: 'mfa',
    component: () => import('@/auth/MfaChallengePage.vue'),
    meta: { layout: 'auth', public: true },
  },

  // --- Legal (public, AppShell-free) ---
  {
    path: '/legal/terms',
    name: 'legal-terms',
    component: () => import('@/modules/legal/TermsPage.vue'),
    meta: { layout: 'auth', public: true },
  },
  {
    path: '/legal/privacy',
    name: 'legal-privacy',
    component: () => import('@/modules/legal/PrivacyPage.vue'),
    meta: { layout: 'auth', public: true },
  },

  // --- Main nav modules ---
  {
    path: '/home',
    name: 'home',
    component: () => import('@/modules/home/HomePage.vue'),
    meta: { nav: 'home', label: 'Home' },
  },
  {
    path: '/people',
    name: 'people',
    component: () => import('@/modules/people/PeoplePage.vue'),
    meta: { nav: 'people', label: 'People' },
  },
  {
    // Proto-v7 unification (navigate() L15205): the standalone Outsourced
    // Workforce route folds into the unified Directory with the Workforce-Type
    // filter pre-applied. Any deep-link / bookmark to /people/outsourced lands
    // on the Directory tab filtered to Outsourced.
    path: '/people/outsourced',
    name: 'people-outsourced',
    redirect: { name: 'people', query: { tab: 'directory', workforce: 'Outsourced' } },
  },
  {
    // Employee profile detail. :id is the full EntityItemId (e.g. "Employee:xxx").
    // Navigated to from DirectoryView with emp.entityItemId; deep-links work
    // because the profile composable fetches by this ID directly.
    path: '/people/profile/:id',
    name: 'people-profile',
    component: () => import('@/modules/people/EmployeeProfilePage.vue'),
    meta: { nav: 'people', label: 'People' },
  },
  {
    // Organization — the active-company hub (V2-FE-2). Tabs in `?tab=`:
    // profile / policies / structure / sites / outsourcing. Structure's inner
    // sub-tabs live in `?sub=`. Scoped to the switcher's activeEmployerId.
    path: '/organization',
    name: 'organization',
    component: () => import('@/modules/organization/OrganizationPage.vue'),
    meta: { nav: 'organization', label: 'Organization' },
  },
  {
    // Outsourcing counterparty (Client / Vendor) detail (V2-FE-3). :id is the
    // full Employer EntityItemId (Kind=OS_CLIENT|OS_VENDOR). Reached from the
    // Organization → Outsourcing tab; deep-links resolve via useCounterparty.
    path: '/organization/outsourcing/:id',
    name: 'organization-counterparty',
    component: () => import('@/modules/organization/CounterpartyProfilePage.vue'),
    meta: { nav: 'organization', label: 'Organization' },
  },
  {
    // A single project (Employer Kind=OS_PROJECT) under a counterparty — lists
    // the project's Org-Chart Position (Node) items. Reached from the
    // counterparty profile's Projects sub-nav.
    path: '/organization/outsourcing/:counterpartyId/projects/:projectId',
    name: 'organization-project',
    component: () => import('@/modules/organization/ProjectDetailPage.vue'),
    meta: { nav: 'organization', label: 'Organization' },
  },
  {
    path: '/work',
    name: 'work',
    component: () => import('@/modules/work/WorkPage.vue'),
    meta: { nav: 'work', label: 'HR Ops' },
  },
  {
    path: '/perform',
    name: 'perform',
    component: () => import('@/modules/perform/PerformPage.vue'),
    meta: { nav: 'perform', label: 'Perform' },
  },
  {
    path: '/fieldops',
    name: 'fieldops',
    component: () => import('@/modules/fieldops/FieldOpsPage.vue'),
    meta: { nav: 'fieldops', label: 'Field Ops' },
  },
  {
    path: '/pay',
    name: 'pay',
    component: () => import('@/modules/pay/PayrollCenterPage.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Active payroll run shell — Wave 2.2a foundation. Per-step views
    // (Steps 1–5) land in 2.3+ and render inside this page.
    path: '/pay/run/:payrollId',
    name: 'pay-run',
    component: () => import('@/modules/pay/PayrollRunPage.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Deep-link mount for Step 4 Approval (Wave 2.7a). The shell-mounted
    // version inside `pay-run` is the primary entry; this dedicated route
    // exists so reviewers can land directly on Step 4 without navigating
    // through Steps 1 → 3.
    path: '/pay/run/:payrollId/step-4',
    name: 'pay-run-step-4',
    component: () => import('@/modules/pay/steps/Step4ApprovalPage.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Read-only completed-payroll detail (Wave 2.1c). Distinct from
    // `pay-run` — that route is an active in-progress run shell.
    path: '/pay/runs/:payrollId',
    name: 'pay-run-detail',
    component: () => import('@/modules/pay/PayrollDetailPage.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Per-employee payslip list for a run (Wave 2.12a — read-path).
    // Drill-down from the Wave 2.8 Step 5 façade. Generate / Distribute
    // / Hold-resolve interactions land in 2.12b/c.
    path: '/pay/runs/:payrollId/payslips',
    name: 'pay-run-payslips',
    component: () => import('@/modules/pay/payslips/PayslipListPage.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Single payslip detail (Wave 2.12a — read-path). Renders the
    // FROZEN Step 3 RunCalculation snapshot the way the printed PDF
    // will. Print + Distribute land in 2.12b/c.
    path: '/pay/runs/:payrollId/payslips/:employeeId',
    name: 'pay-run-payslip',
    component: () => import('@/modules/pay/payslips/PayslipPage.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Step 5 (the "façade" step) read-path. Wave 2.8a renders the
    // Payment + Payslips + Reports + Close sections in read-only mode.
    // Mutation flows land in 2.8b / 2.8c. The route exists as a
    // deep-link target so future surfaces can navigate directly into
    // the step without going through the run-shell stepper first.
    path: '/pay/runs/:payrollId/step-5',
    name: 'pay-run-step-5',
    component: () => import('@/modules/pay/PayrollRunStep5Page.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Templates list (Wave 2.11a). Sub-nav under Pay; surfaced via a
    // link in the Payroll Center header until a real Pay sub-nav lands.
    path: '/pay/templates',
    name: 'pay-templates',
    component: () => import('@/modules/pay/TemplatesPage.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Configurations (Phase A · P13) — proto-port placeholder for the
    // payroll backend's tenant-level config surfaces (pay components,
    // pay groups, NSSF / tax rates, calc rules). The proto itself
    // doesn't ship a Configurations tab; v2 adds it ahead of the
    // backend integration that's currently WIP.
    path: '/pay/configurations',
    name: 'pay-configurations',
    component: () => import('@/modules/pay/ConfigurationsPage.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Pay Input Center (Wave 2.5a). Period + component pickers in the
    // query string so refreshes preserve the active selection.
    path: '/pay/inputs',
    name: 'pay-input-center',
    component: () => import('@/modules/pay/PayInputCenterPage.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Reports Center gallery (Wave 2.10a — read-path). Lists every
    // (PayrollRun × ReportType) cell for the employer with lifecycle
    // status. Generate / regenerate / download land in 2.10b/c.
    path: '/pay/reports',
    name: 'pay-reports',
    component: () => import('@/modules/pay/reports-center/ReportsCenterView.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Off-cycle landing + draft list (Wave 2.9a — read-path). The actual
    // wizard create/edit flow ships in 2.9b; commit in 2.9c.
    path: '/pay/off-cycle',
    name: 'pay-off-cycle',
    component: () => import('@/modules/pay/off-cycle/OffCycleLandingView.vue'),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Off-cycle wizard read-only view (Wave 2.9a). 5 wizard areas
    // surface their current draft state. Edit interactions land in 2.9b.
    path: '/pay/off-cycle/drafts/:draftId',
    name: 'pay-off-cycle-draft',
    component: () => import('@/modules/pay/off-cycle/OffCycleWizardView.vue'),
    props: (route) => ({ draftId: route.params.draftId as string }),
    meta: { nav: 'pay', label: 'Pay' },
  },
  {
    // Billing (provider recharge) — proto `VIEWS.billing` (ARCH-2, between
    // Payroll and Work Mgmt). Fixture-only in this slice. `meta.requiresProvider`
    // gates the route to provider-persona tenants (claim-based `auth.isProvider`);
    // the guard redirects non-providers to Home. Sub-views switch via `?tab=`.
    path: '/billing',
    name: 'billing',
    component: () => import('@/modules/billing/BillingPage.vue'),
    meta: { nav: 'billing', label: 'Billing', requiresProvider: true },
  },
  {
    path: '/workmgmt',
    name: 'workmgmt',
    component: () => import('@/modules/workmgmt/WorkMgmtPage.vue'),
    meta: { nav: 'workmgmt', label: 'Work Mgmt' },
  },
  {
    path: '/insights',
    name: 'insights',
    component: () => import('@/modules/insights/InsightsPage.vue'),
    meta: { nav: 'insights', label: 'Insights' },
  },
  {
    // Admin settings — proto's `admin()` view (4 setting groups × ~3
    // tiles each). Reachable from the More Apps launcher's "Admin"
    // tile. The launcher itself lives at `/admin` (route name
    // `admin`); this nested URL is `/admin/settings` to disambiguate.
    path: '/admin/settings',
    name: 'admin-settings',
    component: () => import('@/modules/moreapps/AdminSettingsPage.vue'),
    meta: { nav: 'moreapps', label: 'Admin' },
  },
  {
    // Admin → Policies landing (V2-policies-to-admin). Moved out of the
    // Organization module. Renders the five per-company policy/setting areas
    // (Attendance · Employment · Leave · Schedule · Localization) as cards.
    // Reached via More Apps → Admin → Policies.
    path: '/admin/policies',
    name: 'admin-policies',
    component: () => import('@/modules/moreapps/policies/AdminPoliciesPage.vue'),
    meta: { nav: 'moreapps', label: 'Admin' },
  },
  {
    // Per-area policy editor (V2-policies-to-admin). `:area` is one of the five
    // POLICY_AREAS slugs; properties render grouped by the v1-mirrored
    // sub-group names. Company-scoped to the switcher's activeEmployerId.
    path: '/admin/policies/:area',
    name: 'admin-policy-area',
    component: () => import('@/modules/moreapps/policies/AdminPolicyAreaPage.vue'),
    meta: { nav: 'moreapps', label: 'Admin' },
  },
  {
    // Tenant / Workspace Settings (V2-FE-5) — top-admin only. Linked from
    // /admin/settings. Business Type (promote/demote-to-provider) is live;
    // Billing / Address / Subscription / Contacts are stubs.
    path: '/admin/tenant-settings',
    name: 'tenant-settings',
    component: () => import('@/modules/moreapps/TenantSettingsPage.vue'),
    meta: { nav: 'moreapps', label: 'Admin' },
  },
  {
    // Users & Roles (IAM) — 1:1 port of the v1 production Users management
    // feature (Cognito users linked to employees + company-admin scope). No
    // separate Roles CRUD exists in production. Reached via More Apps → Admin →
    // Users & Roles. Company-scoped to the switcher's activeEmployerId.
    path: '/admin/users-roles',
    name: 'admin-users-roles',
    component: () => import('@/modules/admin/views/UsersRolesPage.vue'),
    meta: { nav: 'moreapps', label: 'Admin' },
  },
  {
    // The v2 "Admin" route maps to the proto's "More Apps" pill. We
    // keep the internal route name ('admin') and path ('/admin') stable
    // so existing links + auth-store references don't break, but the
    // label + nav-key now match the proto.
    path: '/admin',
    name: 'admin',
    component: () => import('@/modules/moreapps/MoreAppsLauncherPage.vue'),
    meta: { nav: 'moreapps', label: 'More Apps' },
  },

  { path: '/:pathMatch(.*)*', redirect: { name: DEFAULT_LANDING_ROUTE } },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});
